/**
 * React Query hook for cache settings
 *
 * Provides data fetching and mutation for cache configuration
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheSettingsApi } from '../services/cacheSettingsApi';
import type { CacheSettings, CacheSettingsUpdateRequest } from '../types/cacheSettings';

// Query key for cache settings
export const CACHE_SETTINGS_QUERY_KEY = ['cacheSettings'] as const;

/**
 * Hook return type for useCacheSettings
 */
export interface UseCacheSettingsResult {
  /** Cache settings data */
  settings: CacheSettings | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Error from fetching settings */
  error: Error | null;
  /** Function to update cache settings */
  updateSettings: (request: CacheSettingsUpdateRequest) => Promise<CacheSettings>;
  /** Loading state for update mutation */
  isUpdating: boolean;
  /** Error from update mutation */
  updateError: Error | null;
  /** Refetch settings */
  refetch: () => void;
}

/**
 * React Query hook for managing cache settings
 *
 * @returns Cache settings data, loading states, and mutation functions
 *
 * @example
 * ```tsx
 * const { settings, isLoading, updateSettings, isUpdating } = useCacheSettings();
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <form onSubmit={() => updateSettings({ maxSize: 2000 })}>
 *     <input value={settings.maxSize} />
 *   </form>
 * );
 * ```
 */
export function useCacheSettings(): UseCacheSettingsResult {
  const queryClient = useQueryClient();

  // Query for fetching cache settings
  const {
    data: settings,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: CACHE_SETTINGS_QUERY_KEY,
    queryFn: () => cacheSettingsApi.getCacheSettings(),
  });

  // Mutation for updating cache settings
  const {
    mutateAsync: updateSettings,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (request: CacheSettingsUpdateRequest) =>
      cacheSettingsApi.updateCacheSettings(request),
    onSuccess: (updatedSettings) => {
      // Update the cache with the new settings
      queryClient.setQueryData(CACHE_SETTINGS_QUERY_KEY, updatedSettings);
    },
  });

  return {
    settings,
    isLoading,
    error: error as Error | null,
    updateSettings,
    isUpdating,
    updateError: updateError as Error | null,
    refetch,
  };
}
